import React, { useState, useEffect, useCallback } from 'react';
import { Link2, RefreshCw, Trash2, Unlink } from 'lucide-react';
import { colors, API, fmt, authHeaders } from '../utils/theme';
import { Badge, Button, LoadingSpinner } from './UI';

const PLUGGY_CONNECT_URL = 'https://cdn.pluggy.ai/pluggy-connect/v2.11.0/pluggy-connect.js';

const STATUS = {
  UPDATED: { label: 'Conectado', cor: colors.success },
  UPDATING: { label: 'Atualizando', cor: colors.info },
  WAITING_USER_INPUT: { label: 'Aguardando autorização', cor: colors.warning },
  LOGIN_ERROR: { label: 'Erro de login no banco', cor: colors.danger },
  OUTDATED: { label: 'Consentimento expirado', cor: colors.danger },
};

let scriptPluggy = null;
function carregarPluggyConnect() {
  if (window.PluggyConnect) return Promise.resolve(window.PluggyConnect);
  if (!scriptPluggy) {
    scriptPluggy = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = PLUGGY_CONNECT_URL;
      s.async = true;
      s.onload = () => (window.PluggyConnect ? resolve(window.PluggyConnect) : reject(new Error('widget da Pluggy não carregou')));
      s.onerror = () => { scriptPluggy = null; reject(new Error('não foi possível carregar o widget da Pluggy')); };
      document.body.appendChild(s);
    });
  }
  return scriptPluggy;
}

async function chamar(caminho, opcoes = {}) {
  const res = await fetch(`${API}${caminho}`, {
    ...opcoes,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opcoes.headers || {}) },
  });
  if (res.status === 204) return null;
  const dados = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(dados.error || `Erro ${res.status}`);
    err.status = res.status;
    err.dados = dados;
    throw err;
  }
  return dados;
}

const dataBR = (v) => (v ? new Date(v).toLocaleDateString('pt-BR') : '—');
const dataHoraBR = (v) => (v ? new Date(v).toLocaleString('pt-BR') : '—');
const dataIsoBR = (v) => (v ? v.split('-').reverse().join('/') : '—');

const SITUACOES = [
  { v: 'aberta', label: 'Em aberto' },
  { v: 'fechada', label: 'Fechada' },
  { v: 'conciliado', label: 'Conciliado' },
  { v: 'pendente', label: 'Pendente' },
  { v: 'coberto', label: 'Coberto pelo PDF' },
];
const SITUACAO_CONCILIACAO = {
  conciliado: { label: 'conciliado', cor: colors.success },
  coberto: { label: 'coberto pelo PDF', cor: colors.info },
  pendente: { label: 'pendente', cor: colors.warning },
};

function LancamentosOpenFinance({ cartaoId }) {
  const [fatura, setFatura] = useState('');
  const [situacoes, setSituacoes] = useState([]);
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const [texto, setTexto] = useState('');
  const [busca, setBusca] = useState('');
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setBusca(texto), 300);
    return () => clearTimeout(t);
  }, [texto]);

  useEffect(() => {
    const qs = new URLSearchParams();
    if (fatura) qs.set('fatura', fatura);
    if (situacoes.length) qs.set('situacao', situacoes.join(','));
    if (de) qs.set('de', de);
    if (ate) qs.set('ate', ate);
    if (busca.trim()) qs.set('q', busca.trim());
    let vivo = true;
    setErro('');
    chamar(`/cartoes/${cartaoId}/open-finance/lancamentos?${qs}`)
      .then(d => { if (vivo) setDados(d); })
      .catch(e => { if (vivo) setErro(e.message); });
    return () => { vivo = false; };
  }, [cartaoId, fatura, situacoes, de, ate, busca]);

  const alternar = (v) => setSituacoes(s => (s.includes(v) ? s.filter(x => x !== v) : [...s, v]));
  const campo = { padding: '6px 8px', fontSize: 12, borderRadius: 6, border: `1px solid ${colors.border}`,
    background: colors.bg, color: colors.text };
  const lancamentos = (dados && dados.lancamentos) || [];
  const faturas = (dados && dados.faturas) || [];

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
        <select value={fatura} onChange={e => setFatura(e.target.value)} style={campo} aria-label="Fatura">
          <option value="">Todas as faturas</option>
          {faturas.map(f => (
            <option key={f.id} value={f.id}>
              {f.id === 'aberta' ? 'Fatura em aberto'
                : `Vence ${dataIsoBR(f.vencimento)}${f.total_centavos != null ? ` · ${fmt(f.total_centavos / 100)}` : ''}`}
            </option>
          ))}
        </select>
        <input type="date" value={de} onChange={e => setDe(e.target.value)} style={campo} aria-label="De" />
        <input type="date" value={ate} onChange={e => setAte(e.target.value)} style={campo} aria-label="Até" />
        <input type="search" value={texto} onChange={e => setTexto(e.target.value)} style={{ ...campo, flex: 1, minWidth: 160 }}
          placeholder="Buscar descrição ou estabelecimento" />
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        {SITUACOES.map(s => {
          const ativo = situacoes.includes(s.v);
          return (
            <button key={s.v} type="button" onClick={() => alternar(s.v)}
              style={{ ...campo, cursor: 'pointer', fontWeight: ativo ? 700 : 400,
                borderColor: ativo ? colors.primary : colors.border, color: ativo ? colors.primary : colors.textMuted }}>
              {s.label}
            </button>
          );
        })}
      </div>
      {erro && <div style={{ fontSize: 12, color: colors.danger, marginBottom: 8 }}>{erro}</div>}
      <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 6 }}>
        {lancamentos.length} lançamento(s) · soma {fmt(((dados && dados.soma_centavos) || 0) / 100)}
        {dados && dados.total_fatura_centavos != null && ` · total da fatura na Pluggy ${fmt(dados.total_fatura_centavos / 100)}`}
      </div>
      <div style={{ maxHeight: 360, overflowY: 'auto', border: `1px solid ${colors.border}`, borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <tbody>
            {lancamentos.map(l => {
              const sc = SITUACAO_CONCILIACAO[l.situacao_conciliacao] || SITUACAO_CONCILIACAO.pendente;
              return (
                <tr key={l.pluggy_transaction_id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <td style={{ padding: '6px 10px', color: colors.textMuted, whiteSpace: 'nowrap' }}>{dataIsoBR(l.data)}</td>
                  <td style={{ padding: '6px 10px', color: colors.text }}>
                    {l.descricao || l.estabelecimento}
                    {l.estabelecimento && l.descricao && l.estabelecimento !== l.descricao && (
                      <span style={{ color: colors.textMuted }}> · {l.estabelecimento}</span>
                    )}
                    {l.parcelas_total ? ` (${l.parcela_numero}/${l.parcelas_total})` : ''}
                  </td>
                  <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                    <Badge text={l.situacao_fatura === 'fechada' ? 'fechada' : 'em aberto'}
                      color={l.situacao_fatura === 'fechada' ? colors.textMuted : colors.warning} />
                  </td>
                  <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}><Badge text={sc.label} color={sc.cor} /></td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', color: colors.text, whiteSpace: 'nowrap' }}>{fmt(l.valor_centavos / 100)}</td>
                </tr>
              );
            })}
            {dados && !lancamentos.length && (
              <tr><td style={{ padding: 12, color: colors.textMuted, textAlign: 'center' }}>Nenhum lançamento com esses filtros.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function OpenFinanceCartao({ cartaoId, podeConectarNoCadastro, onCriarCartao }) {
  const [estado, setEstado] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState('');
  const [escolha, setEscolha] = useState(null);
  // Conexões que o dono já autorizou em outro cartão dele (28/10/2026): o
  // segundo cartão do mesmo banco só escolhe a conta, sem refazer a Pluggy.
  const [conexoes, setConexoes] = useState([]);
  // Resultado do "apagar o que veio do Open Finance"; a versão remonta a lista.
  const [apagado, setApagado] = useState(null);
  const [versao, setVersao] = useState(0);

  const carregar = useCallback(async (id) => {
    if (!id) { setEstado(null); return; }
    setCarregando(true);
    setErro('');
    try {
      setEstado(await chamar(`/cartoes/${id}/open-finance`));
    } catch (e) {
      setErro(e.message);
    }
    setCarregando(false);
  }, []);

  useEffect(() => { carregar(cartaoId); }, [cartaoId, carregar]);

  useEffect(() => {
    setConexoes([]);
    if (!cartaoId || !estado || !estado.pode_gerenciar || estado.open_finance) return;
    let vivo = true;
    chamar(`/cartoes/${cartaoId}/open-finance/conexoes`)
      .then(d => { if (vivo) setConexoes((d && d.conexoes) || []); })
      .catch(() => {});
    return () => { vivo = false; };
  }, [cartaoId, estado]);

  const vincular = async (id, itemId, accountId) => {
    setOcupado(true);
    setErro('');
    try {
      const novo = await chamar(`/cartoes/${id}/open-finance`, {
        method: 'POST', body: JSON.stringify(accountId ? { itemId, accountId } : { itemId }),
      });
      setEscolha(null);
      setEstado(novo);
    } catch (e) {
      if (e.status === 409 && e.dados && e.dados.escolher) setEscolha({ id, itemId, contas: e.dados.escolher });
      else setErro(e.message);
    }
    setOcupado(false);
  };

  const conectar = async (renovar = false) => {
    setErro('');
    setOcupado(true);
    try {
      const id = cartaoId || await onCriarCartao();
      if (!id) { setOcupado(false); return; }
      const { accessToken, sandbox, itemId } = await chamar(`/cartoes/${id}/open-finance/connect-token`, {
        method: 'POST', body: JSON.stringify({ renovar }),
      });
      const PluggyConnect = await carregarPluggyConnect();
      new PluggyConnect({
        connectToken: accessToken,
        includeSandbox: !!sandbox,
        updateItem: itemId || undefined,
        onSuccess: ({ item }) => vincular(id, item.id),
        onError: (e) => setErro(`Pluggy: ${(e && e.message) || 'falha na conexão'}`),
        onClose: () => setOcupado(false),
      }).init();
    } catch (e) {
      setErro(e.message);
      setOcupado(false);
    }
  };

  const desconectar = async () => {
    const outros = (estado && estado.cartoes_no_mesmo_item) || [];
    const aviso = outros.length
      ? `\n\nA mesma conexão também alimenta: ${outros.map(c => c.nome_cartao).join(', ')}. Eles também serão desconectados.`
      : '';
    if (!window.confirm(`Desconectar o Open Finance deste cartão? O consentimento é revogado no banco e, para voltar, será preciso autorizar de novo.${aviso}`)) return;
    setOcupado(true);
    setErro('');
    try {
      await chamar(`/cartoes/${cartaoId}/open-finance`, { method: 'DELETE' });
      await carregar(cartaoId);
    } catch (e) {
      setErro(e.message);
    }
    setOcupado(false);
  };

  // Conexão feita no cartão errado (03/11/2026): desconectar não leva embora
  // o que já tinha vindo do banco. Isto apaga só deste cartão.
  const apagarDados = async () => {
    if (!window.confirm('Apagar deste cartão tudo o que veio do Open Finance: lançamentos, faturas e o limite do banco?\n\n'
      + 'Lançamento já conciliado ou no fechamento não some: fica marcado como removido.\n'
      + 'Não dá pra desfazer. Quando o cartão certo for conectado, o banco manda os dados dele de novo.')) return;
    setOcupado(true);
    setErro('');
    try {
      setApagado(await chamar(`/cartoes/${cartaoId}/open-finance/dados`, { method: 'DELETE' }));
      await carregar(cartaoId);
      setVersao(v => v + 1);
    } catch (e) {
      setErro(e.message);
    }
    setOcupado(false);
  };

  // Pede à Pluggy uma coleta nova no banco (02/11/2026). A rota existia, mas a
  // tela só tinha "Renovar" — que reabre a autorização, não busca lançamento.
  // Quando a Pluggy termina, o aviso chega e todos os cartões da conexão
  // são sincronizados.
  const atualizarAgora = async () => {
    setErro('');
    setOcupado(true);
    try {
      await chamar(`/cartoes/${cartaoId}/open-finance/atualizar`, { method: 'POST' });
      await carregar(cartaoId);
    } catch (e) {
      setErro(e.message);
    }
    setOcupado(false);
  };

  const caixa = { padding: 14, borderRadius: 8, background: colors.bg, border: `1px solid ${colors.border}` };
  const mudo = { fontSize: 12, color: colors.textMuted };
  const erroBox = erro && (
    <div style={{ ...caixa, borderColor: colors.danger, color: colors.danger, fontSize: 13, marginTop: 12 }}>{erro}</div>
  );

  if (!cartaoId) {
    return (
      <div style={caixa}>
        <div style={{ fontSize: 14, color: colors.text, marginBottom: 6 }}>Conectar o banco via Open Finance (Pluggy)</div>
        {podeConectarNoCadastro ? (
          <>
            <div style={{ ...mudo, marginBottom: 12 }}>O cartão é criado com os dados preenchidos nas outras abas e, em seguida, o banco abre para você autorizar.</div>
            <Button icon={Link2} onClick={() => conectar(false)} disabled={ocupado}>
              {ocupado ? 'Abrindo...' : 'Criar cartão e conectar'}
            </Button>
          </>
        ) : (
          <div style={mudo}>Só o dono do cartão conecta o Open Finance, depois que o cartão estiver criado.</div>
        )}
        {erroBox}
      </div>
    );
  }

  if (carregando && !estado) return <LoadingSpinner />;

  const st = estado && STATUS[estado.status];
  const pode = estado && estado.pode_gerenciar;

  return (
    <div>
      <div style={caixa}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: colors.text }}>
                {estado && estado.open_finance ? (estado.banco || 'Banco conectado') : 'Sem Open Finance'}
              </span>
              {estado && estado.open_finance && (
                <Badge text={st ? st.label : (estado.status || 'Sem status')} color={st ? st.cor : colors.textMuted} />
              )}
            </div>
            {estado && estado.open_finance && (
              <div style={mudo}>
                Consentimento até {dataBR(estado.consentimento_ate)} · última sincronização {dataHoraBR(estado.sync_em)}
              </div>
            )}
            {estado && estado.open_finance && estado.limites && (
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8, fontSize: 12 }}>
                {[
                  ['Limite no banco', estado.limites.limite_total],
                  ['Disponível', estado.limites.disponivel],
                  ['Fatura atual', estado.limites.fatura_atual],
                ].map(([rotulo, v]) => (
                  <span key={rotulo}>
                    <span style={{ color: colors.textMuted }}>{rotulo}: </span>
                    <span style={{ color: colors.text, fontWeight: 700 }}>{v == null ? '—' : fmt(v)}</span>
                  </span>
                ))}
                <span style={{ color: colors.textMuted }}>
                  fecha {dataIsoBR(estado.limites.fechamento)} · vence {dataIsoBR(estado.limites.vencimento)}
                  · cadastro {fmt(estado.limites.limite_cadastro)}
                </span>
              </div>
            )}
            {estado && estado.open_finance && estado.aguardando && (
              <div style={{ fontSize: 12, color: colors.info, marginTop: 4 }}>
                Atualização pedida — aguardando a Pluggy terminar a coleta no banco. Os lançamentos aparecem sozinhos.
              </div>
            )}
            {estado && estado.sync_erro && (
              <div style={{ fontSize: 12, color: colors.warning, marginTop: 4 }}>Última sincronização falhou: {estado.sync_erro}</div>
            )}
            {estado && !pode && (
              <div style={{ ...mudo, marginTop: 4 }}>Só o dono do cartão conecta ou desconecta o Open Finance.</div>
            )}
          </div>
          {estado && estado.open_finance && !pode && (
            <Button size="sm" variant="outline" icon={RefreshCw} onClick={atualizarAgora} disabled={ocupado || estado.aguardando}>
              Atualizar agora
            </Button>
          )}
          {pode && (
            <div style={{ display: 'flex', gap: 8 }}>
              {estado.open_finance ? (
                <>
                  <Button size="sm" icon={RefreshCw} onClick={atualizarAgora} disabled={ocupado || estado.aguardando}
                    title="Pede à Pluggy os lançamentos novos do banco">Atualizar agora</Button>
                  <Button size="sm" variant="outline" icon={Link2} onClick={() => conectar(true)} disabled={ocupado}
                    title="Refaz a autorização no banco (consentimento vencido ou erro de login)">Renovar</Button>
                  <Button size="sm" variant="outline" color={colors.danger} icon={Unlink} onClick={desconectar} disabled={ocupado}>Desconectar</Button>
                </>
              ) : (
                <Button size="sm" icon={Link2} onClick={() => conectar(false)} disabled={ocupado}>
                  {ocupado ? 'Abrindo...' : 'Conectar banco'}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {estado && !estado.open_finance && estado.dados_antigos && (
        <div style={{ ...caixa, marginTop: 12, borderColor: colors.warning }}>
          <div style={{ fontSize: 13, color: colors.text, marginBottom: 4 }}>Ficaram os dados da conexão anterior</div>
          <div style={{ ...mudo, marginBottom: 10 }}>
            {estado.dados_antigos.lancamentos} lançamento(s)
            {estado.dados_antigos.faturas ? ` e ${estado.dados_antigos.faturas} fatura(s)` : ''} do Open Finance continuam
            neste cartão, com o limite que o banco informava. Se a conexão era de outro cartão, apague antes de conectar o certo.
          </div>
          {estado.pode_apagar_dados ? (
            <Button size="sm" variant="outline" color={colors.danger} icon={Trash2} onClick={apagarDados} disabled={ocupado}>
              Apagar o que veio do Open Finance
            </Button>
          ) : (
            <div style={mudo}>Só o dono do cartão ou o ADMIN apaga.</div>
          )}
        </div>
      )}

      {apagado && (
        <div style={{ ...caixa, marginTop: 12, borderColor: colors.success, fontSize: 13, color: colors.text }}>
          Apagado: {apagado.apagados} lançamento(s){apagado.faturas ? ` e ${apagado.faturas} fatura(s)` : ''}, e o limite do banco.
          {apagado.marcados > 0 && ` ${apagado.marcados} já conciliado(s) ou no fechamento ficaram marcados como removidos — confira na Conciliação.`}
        </div>
      )}

      {pode && estado && !estado.open_finance && conexoes.some(cx => cx.contas.length) && (
        <div style={{ ...caixa, marginTop: 12, borderColor: colors.info }}>
          <div style={{ fontSize: 13, color: colors.text, marginBottom: 4 }}>
            Usar uma conexão que você já autorizou
          </div>
          <div style={{ ...mudo, marginBottom: 10 }}>
            Escolha o cartão deste banco — não precisa passar pela Pluggy de novo.
          </div>
          {conexoes.filter(cx => cx.contas.length).map(cx => (
            <div key={cx.item_id} style={{ marginBottom: 8 }}>
              <div style={{ ...mudo, marginBottom: 6, fontWeight: 600 }}>{cx.banco || 'Banco conectado'}</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[...cx.contas].sort((a, b) => Number(b.combina_final) - Number(a.combina_final)).map(c => (
                  <Button key={c.id} size="sm" variant={c.combina_final && !c.vinculado_a ? undefined : 'outline'}
                    disabled={ocupado || !!c.vinculado_a}
                    title={c.vinculado_a ? `Já vinculado ao cartão ${c.vinculado_a.nome_cartao}` : 'Vincular este cartão'}
                    onClick={() => vincular(cartaoId, cx.item_id, c.id)}>
                    {c.nome || 'Cartão'}{c.numero ? ` · ${c.numero}` : ''}
                    {c.vinculado_a ? ` — já é do ${c.vinculado_a.nome_cartao}` : (c.combina_final ? ' — mesmo final' : '')}
                  </Button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {escolha && (
        <div style={{ ...caixa, marginTop: 12, borderColor: colors.warning }}>
          <div style={{ fontSize: 13, color: colors.text, marginBottom: 8 }}>O banco tem mais de um cartão de crédito. Qual é este?</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {escolha.contas.map(c => (
              <Button key={c.id} size="sm" variant="outline" disabled={ocupado}
                onClick={() => vincular(escolha.id, escolha.itemId, c.id)}>
                {c.nome || 'Cartão'} {c.numero ? `· ${c.numero}` : ''}
              </Button>
            ))}
          </div>
        </div>
      )}

      {erroBox}

      {estado && <LancamentosOpenFinance key={versao} cartaoId={cartaoId} />}
    </div>
  );
}
